import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../lib/lang";
import { useSeo } from "../lib/seo";
import { api, fmtUSD } from "../lib/api";

const ROMAN = ["I","II","III","IV","V","VI"];

const HOME_FAQ = [
  {
    en: ["Is this an agency?", "We are a productized brand platform for founders and companies that need senior brand development and direction in a world of infinite output."],
    es: ["¿Esto es una agencia?", "Somos una plataforma de marca productizada para fundadores y compañías que necesitan desarrollo y dirección de marca senior en un mundo de producción infinita."],
  },
  {
    en: ["Is this work AI-generated?", "No. We use AI tools where they are useful, but the direction, judgment, strategy and final decisions come from real people."],
    es: ["¿Este trabajo es generado por IA?", "No. Usamos herramientas de IA donde son útiles, pero la dirección, el criterio, la estrategia y las decisiones finales vienen de personas reales."],
  },
  {
    en: ["What exactly do I receive?", "Brand Solutions: written strategy documents (positioning, narrative, messaging), direction for designers and devs (documents they can ship from), roadmaps that survive the quarter, brand systems and visual universe direction, a clear status tracker in your portal, and a recommended next step at the end of every delivery."],
    es: ["¿Qué recibo exactamente?", "Brand Solutions: documentos de estrategia escritos (posicionamiento, narrativa, mensaje), dirección para diseñadores y devs (documentos ejecutables), roadmaps que sobreviven el trimestre, sistemas de marca y dirección de universo visual, un status tracker claro en tu portal, y un próximo paso recomendado al cierre de cada entrega."],
  },
  {
    en: ["How do we work together?", "Through your private project portal. You upload your materials, we review them, and you receive brand solutions."],
    es: ["¿Cómo trabajamos juntos?", "A través de tu portal de proyecto privado. Subes tus materiales, los revisamos y recibes brand solutions."],
  },
  {
    en: ["What about revisions?", "Brand Development and Brand Strategy include 2 adjustment rounds each. Brand Scan is a diagnosis with no revisions. Brand Partner is request-based. Brand Office is full consulting."],
    es: ["¿Y las revisiones?", "Desarrollo de marca y Estrategia de marca y narrativa incluyen 2 rondas de ajuste. Escaneo de marca es un diagnóstico sin revisiones. Socio de marca se basa en requests. Oficina de marca es consultoría completa."],
  },
  {
    en: ["What's NOT included?", "Ads management, graphic production, content production and unlimited revisions. We give you brand solutions and direction."],
    es: ["¿Qué NO está incluido?", "Gestión de ads, producción gráfica, producción de contenido y revisiones ilimitadas. Damos brand solutions y dirección."],
  },
  {
    en: ["When do I pay?", "Upfront for Brand Scan, Development and Strategy. Monthly for Brand Partner. Brand Office is application-based. Price is provided after fit review."],
    es: ["¿Cuándo pago?", "Por adelantado en Escaneo, Desarrollo y Estrategia de marca. Mensual en Socio de marca. Oficina de marca es por aplicación. El precio se entrega tras revisión de encaje."],
  },
];

const CASES = [
  {
    name: "FILA (Latin)",
    tag: "Sport / Lifestyle",
    cls: "c1",
    img: "/images/cases/fila.png",
    url: "https://www.filalatin.com",
    service: "Brand Office",
    problem: { en: "A regional sport/lifestyle brand needed consistent creative and digital brand direction across markets.", es: "Una marca regional de deporte/lifestyle necesitaba dirección creativa y digital consistente en todos los mercados." },
    solution: { en: "Regional brand strategy, creative direction, and digital brand management.", es: "Estrategia regional de marca, dirección creativa y gestión digital de marca." },
  },
  {
    name: "TRUVAL Corp.",
    tag: "Holding / Hospitality",
    cls: "c2",
    img: "/images/cases/truval.png",
    service: "Brand Strategy & Narrative Universe / Brand Office",
    problem: { en: "A holding/hospitality group needed clearer brand architecture across real estate and hospitality ventures.", es: "Un holding de hospitality necesitaba una arquitectura de marca más clara para sus ventures inmobiliarios y hoteleros." },
    solution: { en: "Defined brand structure, positioning logic, and communication direction for the group and its ventures.", es: "Estructura de marca definida, lógica de posicionamiento y dirección de comunicación para el grupo y sus ventures." },
  },
  {
    name: "PGA Punta Coral",
    tag: "Residential / Golf",
    cls: "c3",
    img: "/images/cases/pga-punta-coral.png",
    service: "Brand Office",
    problem: { en: "A residential and golf destination needed to communicate trust, lifestyle, and destination value with more clarity.", es: "Un destino residencial y de golf necesitaba comunicar confianza, lifestyle y valor de destino con más claridad." },
    solution: { en: "Brand development, creative direction, and brand strategy.", es: "Desarrollo de marca, dirección creativa y estrategia de marca." },
  },
  {
    name: "sōtto",
    tag: "Wellness / Longevity",
    cls: "c4",
    img: "/images/cases/sotto.png",
    url: "https://www.sotto.com",
    service: "Brand Development / Brand Strategy & Narrative Universe / Brand Office",
    problem: { en: "A new auditory longevity venture needed to create a new category and a new behavior around hearing loss.", es: "Un nuevo venture de longevidad auditiva necesitaba crear una nueva categoría y un nuevo comportamiento en torno a la pérdida auditiva." },
    solution: { en: "Category narrative, brand universe, positioning, launch direction, and cultural language around auditory longevity.", es: "Narrativa de categoría, universo de marca, posicionamiento, dirección de lanzamiento y lenguaje cultural alrededor de la longevidad auditiva." },
  },
  {
    name: "Brouillard",
    tag: "Outdoor / Lifestyle",
    cls: "c5",
    img: "/images/cases/brouillard.png",
    url: "https://www.brouillard.co",
    service: "Brand Development / Brand Office",
    problem: { en: "A new lifestyle accessories venture needed a clear world: movement, mountain culture, restraint, and modern utility.", es: "Un nuevo venture de accesorios lifestyle necesitaba un mundo claro: movimiento, cultura de montaña, contención y utilidad moderna." },
    solution: { en: "Creative direction, brand strategy, visual universe, product narrative, and go-to-market direction.", es: "Dirección creativa, estrategia de marca, universo visual, narrativa de producto y dirección de salida al mercado." },
  },
  {
    name: "New Home",
    tag: "Real Estate",
    cls: "c6",
    img: "/images/cases/new-home.png",
    service: "Brand Development / Brand Scan / Brand Office",
    problem: { en: "A real estate platform needed a rebrand and a stronger trust system around certified residential properties.", es: "Una plataforma inmobiliaria necesitaba un rebrand y un sistema de confianza más fuerte alrededor de propiedades residenciales certificadas." },
    solution: { en: "Creative direction, brand strategy and trust system.", es: "Dirección creativa, estrategia de marca y sistema de confianza." },
  },
];

function PricingCard({ s, idx, t, lang, navigate, featured }) {
  const bestFor = {
    "brand-scan": { en: "An existing brand that needs a fast diagnosis.", es: "Una marca existente que necesita un diagnóstico rápido." },
    "brand-development": { en: "New brands, early brands, or ideas.", es: "Marcas nuevas, marcas tempranas o ideas." },
    "brand-strategy": { en: "Existing brands with identity/product that need story, positioning, tone and cultural world.", es: "Marcas existentes con identidad/producto que necesitan historia, posicionamiento, tono y mundo cultural." },
    "brand-partner": { en: "Founders and teams that need ongoing senior brand direction.", es: "Fundadores y equipos que necesitan dirección de marca senior continua." },
  };
  const bullets = {
    "brand-scan": {
      en: ["Brand diagnosis", "Positioning scorecard", "Top problems & opportunities", "30-day action plan", "Recommended next step"],
      es: ["Diagnóstico de marca", "Scorecard de posicionamiento", "Top problemas y oportunidades", "Plan de acción a 30 días", "Próximo paso recomendado"],
    },
    "brand-development": {
      en: ["Positioning + concept", "Naming definition", "Final logo design + export package", "Core brand system: typography, colour, usage direction", "30-day launch plan", "2 adjustment rounds"],
      es: ["Posicionamiento + concepto", "Definición de naming", "Diseño de logo final + paquete de export", "Sistema de marca base: tipografía, color, dirección de uso", "Plan de lanzamiento 30 días", "2 rondas de ajuste"],
    },
    "brand-strategy": {
      en: ["Brand universe", "Tone of voice", "Cultural references", "Messaging pillars", "2 adjustment rounds"],
      es: ["Universo de marca", "Tono de voz", "Referencias culturales", "Pilares de mensaje", "2 rondas de ajuste"],
    },
    "brand-partner": {
      en: ["Includes Brand Scan + Brand Development + Brand Strategy", "4 requests / month", "1 active at a time", "Visual universe direction", "Monthly roadmap"],
      es: ["Incluye Escaneo de marca + Desarrollo de marca + Estrategia de marca", "4 requests / mes", "1 activo a la vez", "Dirección de universo visual", "Roadmap mensual"],
    },
  }[s.slug] || { en: [], es: [] };

  const start = async () => {
    // Pricing CTAs always go to the service detail page; checkout starts from there
    navigate(`/services/${s.slug}`);
  };

  return (
    <div className={`pcard${featured ? " featured" : ""}`} data-testid={`pcard-${s.slug}`}>
      {featured && <div className="pcard-flag">{t("Most chosen", "Más elegido")}</div>}
      <div className="pcard-roman">{ROMAN[idx] || "·"}</div>
      <h3 className="pcard-name">{s.name}</h3>
      <p className="pcard-tagline">{s.tagline}</p>
      {bestFor[s.slug] && (
        <p className="pcard-best">
          <span className="pcard-best-label">{t("Best for", "Ideal para")}</span>
          <span className="pcard-best-text">{t(bestFor[s.slug].en, bestFor[s.slug].es)}</span>
        </p>
      )}
      <div className="pcard-price">
        {s.price ? fmtUSD(s.price) : <em>{t("On request", "Bajo consulta")}</em>}
        {s.billing === "monthly" && <span className="per">/{t("mo", "mes")}</span>}
      </div>
      <ul className="pcard-bullets">
        {(bullets[lang] || bullets.en).map((b) => (
          <li className="pcard-bullet" key={b}>{b}</li>
        ))}
      </ul>
      <div className="pcard-cta">
        <button onClick={start} className="pill solid" data-testid={`pcard-cta-${s.slug}`}>
          {s.billing === "application" ? t("Learn more", "Saber más") : t("See details →", "Ver detalles →")}
        </button>
        <span className="pcard-fit">{t("Fit Guarantee: refund before work begins if we're not the fit.", "Fit Guarantee: reembolso antes de empezar si no encajamos.")}</span>
      </div>
    </div>
  );
}

function Cell({ value, us }) {
  if (value === true) {
    return (
      <span className={`cm-col cm-val ${us ? "cm-us" : ""}`} role="cell" aria-label="included">
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
          <path d="M3 8.3l3.4 3.4L13 4.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (value === "p") {
    return <span className="cm-col cm-val cm-partial" role="cell" aria-label="partial">~</span>;
  }
  return <span className="cm-col cm-val cm-empty" role="cell" aria-label="not included">·</span>;
}

export default function HomePage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);

  useSeo({
    title: lang === "es"
      ? "Brand & Behavior — Plataforma productizada de Brand Strategy & Brand Development"
      : "Brand & Behavior — Productized Brand Strategy & Brand Development Platform",
    description: lang === "es"
      ? "Una plataforma productizada para brand strategy, brand development y dirección senior de marca. Alcance fijo, proceso claro y criterio estratégico senior."
      : "A productized platform for brand strategy, brand development and senior brand direction. Fixed scope, clear process and senior strategic judgment.",
    path: lang === "es" ? "/es" : "/",
  });

  useEffect(() => { api.get(`/services?lang=${lang}`).then((r) => setServices(r.data)).catch(() => {}); }, [lang]);

  const paid = services.filter((s) => s.billing !== "application");
  const office = services.find((s) => s.billing === "application");

  return (
    <main className="app-main" data-testid="home-page">
      {/* 1. Masthead */}
      <section className="masthead">
        <div className="masthead-content">
          <h1>
            {t("Build the ", "Construye la ")}
            <em className="it">{t("brand side", "marca")}</em>
            {t(" of your business, better.", " de tu negocio, mejor.")}
          </h1>
          <p className="sub">
            {t(
              "Fixed-scope brand development, strategic direction, creative direction, and ongoing brand support, delivered through a clear digital process.",
              "Desarrollo de marca de alcance fijo, dirección estratégica, dirección creativa y soporte de marca continuo, entregado a través de un proceso digital claro."
            )}
          </p>
          <div className="cta-row">
            <a href="#pricing" className="pill solid" data-testid="masthead-start">{t("See plans", "Ver planes")}</a>
            <a href="#start3" className="pill ghost" data-testid="masthead-how">{t("How we work", "Cómo trabajamos")}</a>
          </div>
          <div className="trust-strip" data-testid="trust-strip">
            <span><span className="dot" />{t("Fixed scope", "Alcance fijo")}</span>
            <span><span className="dot" />{t("Clear process", "Proceso claro")}</span>
            <span><span className="dot" />{t("No unnecessary calls", "Sin llamadas innecesarias")}</span>
          </div>
        </div>
      </section>

      {/* 1.b Social proof — removed badge logo row per editorial pass; kept clean masthead transition */}

      {/* 2. How we work — 6 steps */}
      <section className="start3 dark hww-dark" id="start3" data-testid="start3-section">
        <div className="head">
          <div className="kicker">{t("How we work", "Cómo trabajamos")}</div>
          <h2>{t("From service to ", "Del servicio a la ")}<em className="it">{t("delivery", "entrega")}</em>{t(", in 6 clear steps.", ", en 6 pasos claros.")}</h2>
          <p className="hww-tag">{t("Brand work, ", "Brand work, ")}<em className="it">{t("productized", "productizado")}</em>.</p>
        </div>
        <div className="howwework-grid">
          {[
            { en: ["Choose your service", "Select the level of brand direction you need: diagnosis, development, narrative, monthly support, or Brand Office."], es: ["Elige tu servicio", "Selecciona el nivel de dirección de marca que necesitas: diagnóstico, desarrollo, narrativa, soporte mensual o Brand Office."] },
            { en: ["Upload your materials", "Submit your website, deck, social channels, brand assets, competitors, references, and the problem you want solved."], es: ["Sube tus materiales", "Envía tu web, deck, canales sociales, activos de marca, competidores, referencias y el problema que quieres resolver."] },
            { en: ["We review and define direction", "We read the brand, identify what is unclear, and define the strategic direction: positioning, narrative, message, offer, visual universe, and next moves."], es: ["Revisamos y definimos dirección", "Leemos la marca, identificamos qué no es claro y definimos la dirección estratégica: posicionamiento, narrativa, mensaje, oferta, universo visual y próximos pasos."] },
            { en: ["Track progress", "Your project moves through a clear system: intake received, materials reviewed, strategy in progress, first direction ready, final delivery."], es: ["Sigue el progreso", "Tu proyecto avanza por un sistema claro: intake recibido, materiales revisados, estrategia en curso, primera dirección lista, entrega final."] },
            { en: ["Receive your brand solution", "You receive a brand solution you can act on, share with your team, or hand to designers, developers, agencies, or internal teams."], es: ["Recibe tu brand solution", "Recibes una brand solution sobre la que puedes actuar, compartir con tu equipo o entregar a diseñadores, developers, agencias o equipos internos."] },
            { en: ["Continue if needed", "After delivery, you can continue with Brand Partner, request a mini-sprint, or apply for Brand Office."], es: ["Continúa si lo necesitas", "Tras la entrega, puedes continuar con Brand Partner, solicitar un mini-sprint o aplicar a Brand Office."] },
          ].map((s, i) => {
            const [h, p] = lang === "es" ? s.es : s.en;
            return (
              <div className="hww-step" key={s.en[0]} data-testid={`hww-step-${i + 1}`}>
                <div className="num-r">{ROMAN[i] || String(i + 1)}</div>
                <h3>{h}</h3>
                <p>{p}</p>
              </div>
            );
          })}
        </div>
        <div className="journey" data-testid="journey-timeline">
          <div className="journey-label">{t("What you'll see in your project portal", "Lo que verás en tu portal de proyecto")}</div>
          <div className="journey-track">
            {[
              { en: "Intake received", es: "Intake recibido", day: "Day 0", dayEs: "Día 0" },
              { en: "Materials reviewed", es: "Materiales revisados", day: "Day 1–2", dayEs: "Día 1–2" },
              { en: "Brand solution in progress", es: "Brand solution en curso", day: "Day 3–7", dayEs: "Día 3–7" },
              { en: "First direction ready", es: "Primera dirección lista", day: "Day 8", dayEs: "Día 8" },
              { en: "Adjustments", es: "Ajustes", day: "Day 9", dayEs: "Día 9" },
              { en: "Final delivery", es: "Entrega final", day: "Day 10", dayEs: "Día 10" },
            ].map((st, i, arr) => (
              <div className={`j-step${i === 0 ? " active" : ""}${i === arr.length - 1 ? " last" : ""}`} key={i}>
                <div className="j-node">
                  <span className="j-num">{i + 1}</span>
                </div>
                <div className="j-body">
                  <div className="j-day">{lang === "es" ? st.dayEs : st.day}</div>
                  <div className="j-label">{lang === "es" ? st.es : st.en}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="journey-foot">
            <span className="j-pill">{t("Updated in real time", "Actualizado en tiempo real")}</span>
            <span className="j-pill">{t("No chasing required", "Sin perseguir")}</span>
            <span className="j-pill">{t("Reference: Brand Development · 10 business days", "Referencia: Brand Development · 10 días hábiles")}</span>
          </div>
        </div>
      </section>

      {/* 3. Problem: why brands fail */}
      {/* 4. Why brands pick us — checkbox comparison matrix */}
      <section className="compare-matrix" data-testid="compare-matrix">
        <div className="head">
          <div className="kicker">{t("Why brands pick us", "Por qué las marcas nos eligen")}</div>
          <h2>{t("Senior brand direction and creativity, without the ", "Dirección y creatividad de marca senior, sin el ")}<em className="it">{t("agency overhead", "overhead de agencia")}</em>{t(" or the undirected AI output.", " ni el output de IA sin dirección.")}</h2>
        </div>
        <div className="cm-table" role="table">
          <div className="cm-row cm-head" role="row">
            <span className="cm-feat"></span>
            <span className="cm-col cm-us">{t("Brand & Behavior", "Brand & Behavior")}</span>
            <span className="cm-col">{t("Agencies", "Agencias")}</span>
            <span className="cm-col">{t("Freelancers", "Freelancers")}</span>
            <span className="cm-col">{t("AI", "IA")}</span>
          </div>
          {[
            { f: { en: "Fixed scope & fixed prices", es: "Alcance y precios fijos" }, us: true, ag: false, fr: false, ih: false },
            { f: { en: "Human strategic direction", es: "Dirección estratégica humana" }, us: true, ag: true, fr: true, ih: false },
            { f: { en: "10-day delivery after intake", es: "Entrega en 10 días tras intake" }, us: true, ag: false, fr: "p", ih: false },
            { f: { en: "No kickoffs, no calls (except Brand Office)", es: "Sin kickoffs ni llamadas (salvo Brand Office)" }, us: true, ag: false, fr: "p", ih: true },
            { f: { en: "Private project portal & status tracker", es: "Portal privado y status tracker" }, us: true, ag: false, fr: false, ih: false },
            { f: { en: "Senior brand point of view", es: "Punto de vista senior de marca" }, us: true, ag: true, fr: false, ih: "p" },
            { f: { en: "Senior creative direction", es: "Dirección creativa senior" }, us: true, ag: true, fr: "p", ih: "p" },
            { f: { en: "Refund before work begins (Fit Guarantee)", es: "Reembolso antes de empezar (Fit Guarantee)" }, us: true, ag: false, fr: false, ih: false },
            { f: { en: "Predictable monthly cost (under $3k)", es: "Coste mensual predecible (menos de $3k)" }, us: true, ag: false, fr: "p", ih: true },
          ].map((row) => (
            <div className="cm-row" role="row" key={row.f.en}>
              <span className="cm-feat" role="cell">{row.f[lang] || row.f.en}</span>
              <Cell value={row.us} us />
              <Cell value={row.ag} />
              <Cell value={row.fr} />
              <Cell value={row.ih} />
            </div>
          ))}
        </div>
        <p className="cm-foot">{t(
          "Check = clearly included. Half = sometimes, depending on the team. Empty = typically not.",
          "Check = claramente incluido. Medio = a veces, según el equipo. Vacío = normalmente no."
        )}</p>
      </section>

      {/* 5. Services / pricing */}
      <section className="pricing" id="pricing">
        <div className="head">
          <div className="kicker">{t("Plans · USD · Fixed scope", "Planes · USD · Alcance fijo")}</div>
          <h2>{t("Pick the level of ", "Elige el nivel de ")}<em className="it">{t("direction", "dirección")}</em>{t(" you need.", " que necesitas.")}</h2>
          <p className="sub">{t("Work through your private project portal. Upload materials, track progress, and receive strategic and creative deliverables.", "Trabaja a través de tu portal privado de proyecto. Sube materiales, sigue el progreso y recibe entregables estratégicos y creativos.")}</p>
        </div>
        <div className="price-cards">
          {paid.map((s, idx) => (
            <PricingCard key={s.slug} s={s} idx={idx} t={t} lang={lang} navigate={navigate} featured={s.slug === "brand-development"} />
          ))}
        </div>
        {office && (
          <div style={{ marginTop: 28 }}>
            <div className="pcard" style={{ minHeight: "auto", display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 20 }} data-testid="pcard-brand-office">
              <div>
                <div className="pcard-roman">V</div>
                <h3 className="pcard-name" style={{ marginTop: 8 }}>{office.name}</h3>
                <p className="pcard-tagline">{t("High-touch strategic leadership and creative direction for founders and teams.", "Liderazgo estratégico y dirección creativa de alta involucración para fundadores y equipos.")}</p>
                <p style={{ fontSize: 12, letterSpacing: ".18em", textTransform: "uppercase", color: "var(--muted)", marginTop: 8 }}>{t("Minimum 3 months · Creative Direction · Brand Strategy · Brand Future · Brand Reputation", "Mínimo 3 meses · Dirección Creativa · Estrategia de Marca · Futuro de Marca · Reputación de Marca")}</p>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="pcard-price" style={{ fontSize: 32 }}><em>{t("Price on request", "Precio bajo consulta")}</em></div>
                <button className="pill solid" style={{ marginTop: 12 }} onClick={() => navigate("/apply")} data-testid="pcard-cta-brand-office">{t("Request Brand Office", "Solicitar Brand Office")}</button>
              </div>
            </div>
          </div>
        )}

        {/* Fit Guarantee policy */}
        <aside className="fit-guarantee" data-testid="fit-guarantee">
          <div className="fit-head">
            <span className="fit-icon">✦</span>
            <h3>{t("Fit Guarantee", "Fit Guarantee")}</h3>
          </div>
          <p className="fit-lead">{t(
            "If after reviewing your intake we believe Brand & Behavior is not the right fit for your project, we'll refund you in full before the work begins.",
            "Si tras revisar tu intake creemos que Brand & Behavior no es el encaje correcto para tu proyecto, te reembolsamos por completo antes de empezar el trabajo."
          )}</p>
          <ul className="fit-policy">
            <li><strong>Brand Scan</strong> · {t("Refund possible only before review starts.", "Reembolso posible solo antes de iniciar el review.")}</li>
            <li><strong>Brand Development / Narrative Universe</strong> · {t("Refund possible only before materials are reviewed. Once first direction starts, no refund.", "Reembolso posible solo antes de revisar materiales. Una vez iniciada la primera dirección, sin reembolso.")}</li>
            <li><strong>Brand Partner</strong> · {t("Cancel anytime before next billing cycle. No refund for active month.", "Cancela en cualquier momento antes del próximo ciclo. Sin reembolso del mes activo.")}</li>
            <li><strong>Brand Office</strong> · {t("Custom agreement · deposit · minimum 3 months.", "Acuerdo personalizado · depósito · mínimo 3 meses.")}</li>
          </ul>
          <Link to="/terms" className="fit-link" data-testid="fit-terms-link">{t("Read full Terms & Policies", "Leer Términos y Políticas completos")} →</Link>
        </aside>
      </section>

      {/* 6. What you receive — moved to FAQ */}

      {/* 7. Comparison — replaced by checkbox matrix above */}

      {/* 8. Brands shaped */}
      <section className="cases">
        <div className="head">
          <div className="kicker">{t("Selected Brand Office projects", "Proyectos seleccionados de Brand Office")}</div>
          <h2>{t("Brands we are ", "Marcas que estamos ")}<em className="it">{t("shaping", "formando")}</em>.</h2>
        </div>
        <div className="case-grid">
          {CASES.map((c) => (
            <div className="case-card" key={c.name} data-testid={`case-${c.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>
              <div
                className={`case-img ${c.cls}${c.img ? " has-photo" : ""}`}
                style={c.img ? { backgroundImage: `url(${c.img})` } : undefined}
              />
              <div className="case-body">
                <div className="case-tag">{c.tag}</div>
                <h3 className="case-name">{c.name}</h3>
                {c.url && (
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="case-url"
                    data-testid={`case-link-${c.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                  >
                    {c.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  </a>
                )}
                <div className="case-row">
                  <span className="case-row-label">{t("Problem", "Problema")}</span>
                  <p className="case-row-text">{c.problem[lang] || c.problem.en}</p>
                </div>
                <div className="case-row">
                  <span className="case-row-label">{t("Solution", "Solución")}</span>
                  <p className="case-row-text">{c.solution[lang] || c.solution.en}</p>
                </div>
                <div className="case-row">
                  <span className="case-row-label">{t("Service", "Servicio")}</span>
                  <p className="case-row-service">{c.service}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 28 }}>
          <Link to="/about" className="pill ghost" data-testid="cases-about">{t("About us", "Sobre nosotros")} →</Link>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="faq-inline">
        <div className="head">
          <div className="kicker">FAQ</div>
          <h2>{t("How we work, clearly.", "Cómo trabajamos.")}</h2>
        </div>
        {HOME_FAQ.map((qa, i) => {
          const [q, a] = lang === "es" ? qa.es : qa.en;
          const isOpen = openFaq === i;
          return (
            <div className="faq-row" key={qa.en[0]}>
              <button onClick={() => setOpenFaq(isOpen ? -1 : i)} aria-expanded={isOpen} className="faq-q-btn" data-testid={`home-faq-${i}`}>
                <span>{q}</span>
                <span className="faq-q-plus">{isOpen ? "—" : "+"}</span>
              </button>
              {isOpen && <div className="faq-a">{a}</div>}
            </div>
          );
        })}
      </section>

      {/* 10. Final CTA */}
      <section className="cta-block">
        <h2>{t("Ready to work on your brand?", "¿Listos para trabajar en tu marca?")}</h2>
        <p>{t("Start with a Brand Scan, scale into Brand Development, or request Brand Office.", "Empieza con un Escaneo de marca, escala a Desarrollo de marca o solicita Oficina de marca.")}</p>
        <Link to="/services" className="pill solid" data-testid="closing-cta">{t("Start your project", "Iniciar tu proyecto")}</Link>
      </section>

      {/* 11. Newsletter */}
      <NewsletterSection t={t} lang={lang} />

      {/* Sticky compact CTA */}
      <StickyCTA t={t} />
    </main>
  );
}

function NewsletterSection({ t, lang }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | ok | exists | error
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || state === "sending") return;
    setState("sending");
    try {
      const { data } = await api.post("/newsletter", { email: email.trim() });
      setState(data.already_subscribed ? "exists" : "ok");
      setEmail("");
    } catch (err) {
      console.error("Newsletter signup failed:", err);
      setState("error");
    }
  };
  return (
    <section className="newsletter-dark" data-testid="newsletter-section">
      <div className="newsletter-inner">
        <div className="kicker">{t("Brand thinking, monthly.", "Brand thinking, mensual.")}</div>
        <h2>
          {t("Creative and strategic notes from inside the ", "Notas creativas y estratégicas desde dentro del ")}
          <em className="it">{t("brand room", "brand room")}</em>.
        </h2>
        <p className="newsletter-sub">
          {t(
            "One short read each month: signals we are tracking, brand moves we admire, and ideas you can apply this week. No spam.",
            "Una lectura corta al mes: señales que seguimos, movimientos de marca que admiramos, e ideas que puedes aplicar esta semana. Sin spam."
          )}
        </p>
        <form className="newsletter-form" onSubmit={submit}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={lang === "es" ? "tu@email.com" : "you@email.com"}
            required
            disabled={state === "sending"}
            data-testid="newsletter-email"
          />
          <button type="submit" className="pill solid newsletter-btn" disabled={state === "sending"} data-testid="newsletter-submit">
            {state === "sending"
              ? (lang === "es" ? "Enviando…" : "Sending…")
              : t("Subscribe →", "Suscribirme →")}
          </button>
        </form>
        {state === "ok" && (
          <p className="newsletter-feedback ok" data-testid="newsletter-ok">
            {t("Thanks. You'll hear from us soon.", "Gracias. Pronto sabrás de nosotros.")}
          </p>
        )}
        {state === "exists" && (
          <p className="newsletter-feedback ok" data-testid="newsletter-exists">
            {t("You are already on the list.", "Ya estás en la lista.")}
          </p>
        )}
        {state === "error" && (
          <p className="newsletter-feedback err" data-testid="newsletter-err">
            {t("Something went wrong. Try again.", "Algo salió mal. Inténtalo de nuevo.")}
          </p>
        )}
      </div>
    </section>
  );
}

function StickyCTA({ t }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight - 200;
      setVisible(y > 700 && y < max);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className={`sticky-cta${visible ? " show" : ""}`} data-testid="sticky-cta">
      <div className="sticky-cta-text">
        <span className="sticky-cta-eyebrow">{t("Productized brand service", "Servicio de marca productizado")}</span>
        <span className="sticky-cta-title">{t("From $700 · Brand solutions · Fit Guarantee", "Desde $700 · Brand solutions · Fit Guarantee")}</span>
      </div>
      <a href="#pricing" className="pill solid" data-testid="sticky-cta-btn">{t("See plans →", "Ver planes →")}</a>
    </div>
  );
}
